import { useEffect, useState } from "react";

/**
 * Detects whether the iOS on-screen keyboard (OSK) is open.
 *
 * iOS Safari does not expose any event for the software keyboard opening or
 * closing, and (unlike Android/desktop) it pushes the whole viewport up without
 * resizing it. We infer the keyboard state from focus on keyboard-triggering
 * inputs via the global `focusin` / `focusout` events.
 *
 * See: https://martijnhols.nl/blog/how-to-detect-the-on-screen-keyboard-in-ios-safari
 *
 * To avoid false positives from a hardware keyboard (where focusing an input
 * does not shrink the viewport), we additionally require the visual viewport to
 * actually be smaller than the layout viewport while the input is focused.
 */
const isKeyboardInput = (element: HTMLElement) =>
	(element.tagName === "INPUT" &&
		!["button", "submit", "checkbox", "file", "image", "radio", "range", "color"].includes(
			(element as HTMLInputElement).type
		)) ||
	element.tagName === "TEXTAREA" ||
	element.isContentEditable;

export function useIsOnScreenKeyboardOpen() {
	const [isOpen, setIsOpen] = useState(false);

	useEffect(() => {
		const handleFocusIn = (event: FocusEvent) => {
			const target = event.target as HTMLElement | null;
			if (!target || !isKeyboardInput(target)) return;

			// A visual viewport that is (or becomes) shorter than the layout
			// viewport means a software keyboard is covering part of the screen.
			// If there is no visual viewport API, assume the OSK is open on focus.
			const viewport = window.visualViewport;
			if (!viewport) {
				setIsOpen(true);
				return;
			}

			const checkOpen = () => {
				if (viewport.height < window.innerHeight) setIsOpen(true);
			};
			// The viewport shrinks a frame or two after focus, so check on the
			// next viewport resize as well as immediately.
			checkOpen();
			viewport.addEventListener("resize", checkOpen, { once: true });
		};

		const handleFocusOut = (event: FocusEvent) => {
			const target = event.target as HTMLElement | null;
			if (!target || !isKeyboardInput(target)) return;
			setIsOpen(false);
		};

		document.addEventListener("focusin", handleFocusIn);
		document.addEventListener("focusout", handleFocusOut);
		return () => {
			document.removeEventListener("focusin", handleFocusIn);
			document.removeEventListener("focusout", handleFocusOut);
		};
	}, []);

	return isOpen;
}
